import type { QueryPlaylistInput, PlaylistSort, SortDirection } from '@scio3/shared-types';
import { MODES } from '@scio3/shared-types';
import type { PlaylistSortField } from '../stores/playlist-filter-store';

interface PlaylistFilterState {
  searchText: string;
  searchScope: 'all' | 'folder';
}

/**
 * Converts playlist filter store state to GraphQL QueryPlaylistInput.
 * Note: 'shared' mode is handled at the hook level (useSharedFolders), not here.
 */
export function buildPlaylistQueryInput(
  filters: PlaylistFilterState,
  currentPath: string | null,
  teamId?: string,
  /**
   * Favorites location: show EVERY favorited playlist regardless of which folder it lives in.
   * Omit the path filter entirely — a `path: null` would restrict to ROOT, and any favorite
   * inside a folder has a real path, so it would never appear. Mirrors `buildAssetQueryInput`.
   */
  isFavorites = false,
): QueryPlaylistInput {
  const query: QueryPlaylistInput = {};

  // Path filter — null for root (only root-level playlists), string for subfolder.
  // When searching with scope 'all', omit path to search globally.
  // Skipped entirely in the Favorites location (all favorites, any folder).
  // query.teamId is coupled with path — only set when path is truthy.
  if (isFavorites) {
    // No path filter — a favorite lives wherever it lives.
  } else if (filters.searchText && filters.searchScope === 'all') {
    // No path filter — search across all folders
  } else if (currentPath && currentPath !== '/' && currentPath !== MODES.SHARE_WITH_ME) {
    query.path = currentPath;
    if (teamId) query.teamId = teamId;
  } else {
    query.path = null;
  }

  // Search text → name regex
  if (filters.searchText) {
    query.name_regex = filters.searchText;
  }

  // Note: favoritesOnly is a control flag sent via extendParams at the hook level (not a query
  // field), and the "rest" exclusion is the sibling extendParams.excludeFavorites flag (server-resolved _id $nin).

  return query;
}

/**
 * Converts sort field + direction to GraphQL PlaylistSort.
 */
export function buildPlaylistSortInput(
  sortField: PlaylistSortField,
  sortDirection: SortDirection,
): PlaylistSort {
  return { [sortField]: sortDirection };
}
